import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, isAdmin, isDoctor } from '@/providers/AuthProvider';
import { UnauthorizedPage } from '@/pages/public/UnauthorizedPage';

interface AuthGuardProps {
  children: ReactNode;
  requiredRole: 'doctor' | 'admin';
}

export function AuthGuard({ children, requiredRole }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex items-center gap-3 text-ink-muted">
          <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  // Not signed in → bounce to login with `?next=` so we can return after auth
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  const roleOk =
    requiredRole === 'doctor' ? isDoctor(user.role) : isAdmin(user.role);

  // Signed in, wrong role → show a proper 403 page (not a silent redirect)
  if (!roleOk) {
    return <UnauthorizedPage attemptedPath={location.pathname} requiredRole={requiredRole} />;
  }

  return <>{children}</>;
}
